import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2, Database, Loader2, RefreshCw, Terminal } from 'lucide-react';
import { api, apiClient } from '@/api/apiClient';
import { toast } from 'sonner';

export function MigrationsManagement() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');

  const checkStatus = async () => {
    setLoading(true);
    setOutput('');
    setError('');
    try {
      const response = await api.functions.invoke('migrations', 'status', {});
      setStatus(response);
      setOutput(response.output || '');
      setError(response.error || '');
      if (response.success) {
        toast.success('Migration status retrieved');
      } else {
        toast.error('Failed to get migration status');
      }
    } catch (err) {
      toast.error('Error checking migration status');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deployMigrations = async () => {
    if (!confirm('Deploy all pending migrations? This will modify the database.')) return;
    
    setLoading(true);
    setOutput('');
    setError('');
    try {
      const response = await api.functions.invoke('migrations', 'deploy', {});
      setOutput(response.output || '');
      setError(response.error || '');
      if (response.success) {
        toast.success('Migrations deployed successfully');
      } else {
        toast.error('Migration deployment failed');
      }
    } catch (err) {
      toast.error('Error deploying migrations');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadSchema = async () => {
    setLoading(true);
    setOutput('');
    setError('');
    try {
      const response = await apiClient.get('/migrations/schema-export', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'db-schema.json');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Schema exported');
    } catch (err) {
      toast.error('Failed to export schema');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resolveFailedMigration = async (migration, action) => {
    if (!confirm(`Mark migration "${migration}" as ${action}?`)) return;
    
    setLoading(true);
    setOutput('');
    setError('');
    try {
      const response = await api.functions.invoke('migrations', 'resolve', { migration, action });
      setOutput(response.output || '');
      setError(response.error || '');
      if (response.success) {
        toast.success(`Migration marked as ${action}`);
        await checkStatus();
      } else {
        toast.error('Failed to resolve migration');
      }
    } catch (err) {
      toast.error('Error resolving migration');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Database Migrations</h1>
        <p className="text-slate-600">Manage Prisma database migrations</p>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Migration Controls
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button onClick={checkStatus} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Check Status
              </Button>
              <Button onClick={deployMigrations} disabled={loading} variant="default">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Deploy Migrations
              </Button>
              <Button onClick={downloadSchema} disabled={loading} variant="outline">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                Download Schema
              </Button>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  <div className="font-semibold mb-1">Error</div>
                  <pre className="text-xs overflow-auto">{error}</pre>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {output && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Terminal className="w-5 h-5" />
                Output
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs bg-slate-900 text-green-400 p-4 rounded-lg overflow-auto max-h-96 font-mono">
                {output}
              </pre>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Failed Migration Recovery</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              If a migration fails, you can mark it as resolved before deploying new migrations.
            </p>
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  type="text"
                  id="failedMigration"
                  placeholder="20260203_add_proposal_settings"
                  className="flex-1 min-w-0 px-3 py-2 border rounded-md text-sm"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const migration = document.getElementById('failedMigration').value;
                    if (migration) resolveFailedMigration(migration, 'rolled-back');
                  }}
                  disabled={loading}
                >
                  Mark as Rolled Back
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const migration = document.getElementById('failedMigration').value;
                    if (migration) resolveFailedMigration(migration, 'applied');
                  }}
                  disabled={loading}
                >
                  Mark as Applied
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Common failed migration: <code className="bg-slate-100 px-1 py-0.5 rounded">20260203_add_proposal_settings</code>
              </p>
            </div>
          </CardContent>
        </Card>

        <Alert>
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            <strong>Warning:</strong> These operations directly modify your production database.
            Always backup your database before running migrations in production.
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}

export default MigrationsManagement;
